/**
 * Barrister Public Profile
 * Premium chambers management aesthetic — structured, authoritative, information-rich.
 * Every value is live from /me/public-profile.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CircleNotch, PencilSimple, CaretDown, CaretUp } from '@phosphor-icons/react';
import { fetchPublicProfile } from '@/lib/meApi';
import { BackButton } from '@/components/shared/BackButton';
import { QueryErrorState } from '@/components/shared/QueryErrorState';

// ─── Helpers ──────────────────────────────────────────────────

const ROLE: Record<string, string> = {
  silk:        "King's Counsel",
  junior:      'Barrister',
  pupil:       'Pupil Barrister',
  door_tenant: 'Door Tenant',
};

function initials(f: string, l: string) {
  return `${f[0] ?? ''}${l[0] ?? ''}`.toUpperCase();
}

// ─── Sidebar label+value row ──────────────────────────────────

function SideRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[88px_1fr] gap-2 py-1.5 border-b border-stone-100 dark:border-slate-800 last:border-0">
      <span className="text-[10px] sm:text-[9.5px] font-semibold uppercase tracking-[0.14em] text-stone-400 dark:text-slate-500 pt-px">{label}</span>
      <span className="text-xs text-stone-700 dark:text-slate-300 leading-snug">{value}</span>
    </div>
  );
}

// ─── Sidebar section ──────────────────────────────────────────

function SideSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 text-[10px] sm:text-[9px] font-bold uppercase tracking-[0.2em] text-stone-400 dark:text-slate-500">{title}</p>
      {children}
    </div>
  );
}

// ─── Content section label ────────────────────────────────────

function SectionMark({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <span className="text-[10px] sm:text-[9.5px] font-bold uppercase tracking-[0.2em] text-stone-400 dark:text-slate-500">{children}</span>
      <span className="flex-1 border-b border-stone-100 dark:border-slate-800" />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// PAGE
// ══════════════════════════════════════════════════════════════

export default function PublicProfilePage() {
  const navigate = useNavigate();
  const [expandedCase, setExpandedCase] = useState<number | null>(null);

  const { data: profile, isLoading, isError, refetch } = useQuery({
    queryKey: ['public-profile'],
    queryFn: fetchPublicProfile,
    staleTime: 0,
    retry: 1,
  });

  if (isLoading) return (
    <div className="flex h-48 items-center justify-center">
      <CircleNotch size={18} className="animate-spin text-stone-300 dark:text-slate-600" />
    </div>
  );

  // A failed fetch also leaves `profile` undefined, and the line below then told a silk that
  // public profiles are "only available for barristers" — a connection problem dressed up as a
  // statement about who they are.
  if (isError) return (
    <div className="py-10">
      <QueryErrorState what="your public profile" onRetry={() => void refetch()} />
    </div>
  );

  if (!profile?.barrister) return (
    <p className="py-20 text-center text-sm text-stone-400 dark:text-slate-500">
      Public profile is only available for barristers.
    </p>
  );

  const { barrister: b, stats, chambers } = profile;
  const name = profile.displayName || `${profile.firstName} ${profile.lastName}`;
  const isSilk = profile.memberType === 'silk';
  const roleLabel = ROLE[profile.memberType] ?? profile.memberType;

  // Credential line under name
  const credParts = [
    roleLabel,
    b.callYear  ? `Called ${b.callYear}`  : null,
    b.silkYear  ? `Silk ${b.silkYear}`    : null,
    b.inn       ?? null,
  ].filter(Boolean);

  // Career milestones — from real DB data only
  const milestones: { year: number; event: string }[] = [];
  if (b.callYear) milestones.push({ year: b.callYear, event: 'Called to the Bar' });
  if (b.silkYear) milestones.push({ year: b.silkYear, event: `Appointed ${isSilk ? "King's Counsel" : 'Senior Counsel'}` });
  b.notableCases.forEach(nc => {
    if (!milestones.find(m => m.year === nc.year))
      milestones.push({ year: nc.year, event: `Notable matter: ${nc.title}` });
  });
  milestones.sort((a, b) => a.year - b.year);

  return (
    <div className="mx-auto max-w-5xl pb-20">

      <BackButton onClick={() => navigate(-1)} />

      {/* ══ HEADER ════════════════════════════════════════════════ */}
      {/* The navy rule at top is the single most important design decision — */}
      {/* it immediately signals "premium legal institution" */}
      <div className="border-t-[3px] border-[#1a2f4e] bg-white dark:bg-card">

        {/* Identity block */}
        <div className="flex items-start gap-4 px-0 pt-6 pb-5 sm:gap-6 sm:pt-7">

          {/* Monogram — small, square, not heroic */}
          <div className="h-[56px] w-[56px] sm:h-[68px] sm:w-[68px] shrink-0 rounded-[6px] bg-stone-100 dark:bg-slate-800 flex items-center justify-center text-[17px] sm:text-[20px] font-semibold text-stone-500 dark:text-slate-400 select-none">
            {profile.avatarUrl
              ? <img src={profile.avatarUrl} alt={name} className="h-full w-full rounded-[6px] object-cover" />
              : initials(profile.firstName, profile.lastName)
            }
          </div>

          {/* Name + credentials */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:gap-4">
              <div>
                {/* Name — dominant but not screaming */}
                <h1 className="text-[22px] sm:text-[27px] font-semibold tracking-tight text-stone-900 dark:text-white leading-tight sm:leading-none">
                  {name}
                  {isSilk && (
                    <span className="ml-2.5 text-sm font-semibold tracking-wide text-[#1a2f4e]">KC</span>
                  )}
                </h1>
                {/* Credential line */}
                <p className="mt-2 text-xs text-stone-500 dark:text-slate-400 tracking-wide">
                  {credParts.join('  ·  ')}
                </p>
                {/* Chambers */}
                <p className="mt-1 text-xs text-stone-400 dark:text-slate-500">{chambers.name}</p>
              </div>

              {/* Actions — restrained, text-weight */}
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 shrink-0 sm:pt-1">
                <button
                  onClick={() => navigate('/barrister/settings/section/account')}
                  className="flex items-center gap-1.5 text-xs text-stone-400 dark:text-slate-500 hover:text-[#1a2f4e] dark:hover:text-slate-300 transition-colors">
                  <PencilSimple size={11} />Edit profile
                </button>
                <button
                  onClick={() => navigate('/barrister/settings/public-profile')}
                  className="flex items-center gap-1.5 text-xs text-stone-400 dark:text-slate-500 hover:text-[#1a2f4e] dark:hover:text-slate-300 transition-colors">
                  <PencilSimple size={11} />Visibility & notable cases
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip — warm stone background, operational numbers */}
        <div className="border-t border-stone-200 dark:border-slate-700/50 bg-stone-50 dark:bg-slate-800/40 -mx-[1px] px-0 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 sm:gap-x-7">
          {b.visibility.matters && (
            <span className="text-xs text-stone-500 dark:text-slate-400">
              <span className="font-semibold text-stone-800 dark:text-slate-200">{stats.activeMatters}</span> active matter{stats.activeMatters !== 1 ? 's' : ''}
            </span>
          )}
          <span className="text-xs text-stone-500 dark:text-slate-400">
            <span className="font-semibold text-stone-800 dark:text-slate-200">{stats.closedMatters}</span> closed
          </span>
          {b.visibility.judgments && stats.judgments > 0 && (
            <span className="text-xs text-stone-500 dark:text-slate-400">
              <span className="font-semibold text-stone-800 dark:text-slate-200">{stats.judgments}</span> judgment{stats.judgments !== 1 ? 's' : ''}
            </span>
          )}
          {b.visibility.winRate && stats.winRate !== null && (
            <span className="text-xs text-stone-500 dark:text-slate-400">
              <span className="font-semibold text-stone-800 dark:text-slate-200">{stats.winRate}%</span> win rate
            </span>
          )}
          {/* Availability — rightmost, most actionable */}
          <span className="sm:ml-auto flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Available for instruction
          </span>
        </div>
      </div>

      {/* ══ BODY ══════════════════════════════════════════════════ */}
      <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-[1fr_224px] md:gap-10">

        {/* LEFT — primary content ───────────────────────────── */}
        <div className="space-y-8 sm:space-y-10 min-w-0">

          {/* Biography */}
          {b.biography && (
            <div>
              <SectionMark>Biography</SectionMark>
              <p className="text-[13.5px] leading-[1.82] text-stone-600 dark:text-slate-300 max-w-2xl">
                {b.biography}
              </p>
            </div>
          )}

          {/* Practice areas */}
          {b.practiceAreas.length > 0 && (
            <div>
              <SectionMark>Practice Areas</SectionMark>
              <div className="space-y-1">
                {b.practiceAreas.map(a => (
                  <div key={a.id} className="flex items-baseline justify-between border-b border-stone-100 dark:border-slate-800 py-2 last:border-0">
                    <span className="text-sm text-stone-700 dark:text-slate-300">{a.name}</span>
                    <span className="text-xs capitalize text-stone-400 dark:text-slate-500">{a.level}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notable matters — legal archive format */}
          {b.notableCases.length > 0 && (
            <div>
              <SectionMark>Notable Matters</SectionMark>
              <div className="space-y-0">
                {b.notableCases.map((nc, i) => {
                  const open = expandedCase === i;
                  return (
                    <div key={i} className="border-b border-stone-100 dark:border-slate-800 last:border-0">
                      <button
                        onClick={() => setExpandedCase(open ? null : i)}
                        className="w-full py-3.5 text-left group flex items-start justify-between gap-4 hover:bg-stone-50 dark:hover:bg-slate-800/50 rounded sm:-mx-3 sm:px-3 transition-colors">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
                            <span className="text-sm font-medium text-stone-800 dark:text-slate-200">{nc.title}</span>
                            <span className="text-xs text-stone-400 dark:text-slate-500">{nc.citation}</span>
                            <span className="text-xs text-stone-400 dark:text-slate-500">{nc.year}</span>
                          </div>
                          {nc.summary && (
                            <p className="mt-1 text-xs text-stone-500 dark:text-slate-400 leading-relaxed">{nc.summary}</p>
                          )}
                        </div>
                        <span className="shrink-0 mt-0.5 text-stone-300 dark:text-slate-600 group-hover:text-stone-500 dark:group-hover:text-slate-400 transition-colors">
                          {open ? <CaretUp size={12} /> : <CaretDown size={12} />}
                        </span>
                      </button>

                      {open && (
                        <div className="mb-3 sm:ml-3 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4 rounded bg-stone-50 dark:bg-slate-800/60 px-4 py-3 text-xs">
                          <div>
                            <p className="text-[10px] sm:text-[9px] uppercase tracking-widest text-stone-400 dark:text-slate-500 mb-1">Role</p>
                            <p className="text-stone-600 dark:text-slate-300">Lead Counsel</p>
                          </div>
                          <div>
                            <p className="text-[10px] sm:text-[9px] uppercase tracking-widest text-stone-400 dark:text-slate-500 mb-1">Court</p>
                            <p className="text-stone-600 dark:text-slate-300">{nc.citation.includes('TCC') ? 'Technology & Construction Court' : nc.citation.includes('EWCA') ? 'Court of Appeal' : nc.citation.includes('UKSC') ? 'Supreme Court' : 'High Court'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] sm:text-[9px] uppercase tracking-widest text-stone-400 dark:text-slate-500 mb-1">Year</p>
                            <p className="text-stone-600 dark:text-slate-300">{nc.year}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Career timeline — only from real DB data */}
          {milestones.length > 0 && (
            <div>
              <SectionMark>Career Milestones</SectionMark>
              <div className="space-y-0">
                {milestones.map((m, i) => (
                  <div key={i} className="flex items-start gap-5 border-b border-stone-100 dark:border-slate-800 py-2.5 last:border-0">
                    <span className="shrink-0 w-10 text-xs font-semibold tabular-nums text-stone-800 dark:text-slate-200">{m.year}</span>
                    <span className="text-[12.5px] text-stone-600 dark:text-slate-300">{m.event}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — sidebar data rail ────────────────────────── */}
        <div className="border-t pt-6 md:border-t-0 md:border-l md:pt-0 border-stone-200 dark:border-slate-800 md:pl-7 space-y-6 text-xs">

          {/* Professional registration */}
          <SideSection title="Registration">
            {b.bsbAuthNumber && <SideRow label="BSB No." value={<span className="">{b.bsbAuthNumber}</span>} />}
            {b.inn            && <SideRow label="Inn" value={b.inn} />}
            {b.callYear       && <SideRow label="Called" value={b.callYear} />}
            {b.silkYear       && <SideRow label="Silk" value={b.silkYear} />}
          </SideSection>

          {/* Qualifications */}
          {b.qualifications.length > 0 && (
            <SideSection title="Qualifications">
              <ul className="space-y-1.5">
                {b.qualifications.map((q, i) => (
                  <li key={i} className="text-xs text-stone-600 dark:text-slate-300 leading-snug pl-2 border-l border-stone-200 dark:border-slate-700">
                    {q}
                  </li>
                ))}
              </ul>
            </SideSection>
          )}

          {/* Languages */}
          {b.languages.length > 0 && (
            <SideSection title="Languages">
              <p className="text-xs text-stone-600 dark:text-slate-300">{b.languages.join(', ')}</p>
            </SideSection>
          )}

          {/* Contact */}
          <SideSection title="Contact">
            <div className="space-y-1.5">
              <p className="text-xs text-stone-600 dark:text-slate-300 break-all">{profile.email}</p>
              {profile.phone && <p className="text-xs text-stone-600 dark:text-slate-300">{profile.phone}</p>}
            </div>
          </SideSection>

          {/* Practice statistics */}
          <SideSection title="Practice">
            {b.visibility.matters    && <SideRow label="Active"  value={stats.activeMatters} />}
                                          <SideRow label="Closed"  value={stats.closedMatters} />
            {b.visibility.judgments  && <SideRow label="Judgments" value={stats.judgments} />}
            {b.visibility.winRate && stats.winRate !== null
              ? <SideRow label="Win rate" value={`${stats.winRate}%`} />
              : stats.closedMatters < 3 && <SideRow label="Win rate" value={<span className="text-stone-400 dark:text-slate-500 italic">Insufficient data</span>} />
            }
          </SideSection>

          {/* Chambers */}
          <SideSection title="Practice Base">
            <p className="text-xs text-stone-600 dark:text-slate-300 leading-snug">{chambers.name}</p>
            {b.publicSlug && (
              <p className="mt-1 text-xs text-stone-400 dark:text-slate-500 break-all">{chambers.slug}/{b.publicSlug}</p>
            )}
          </SideSection>
        </div>
      </div>
    </div>
  );
}
